import { Component, inject, OnInit, signal, viewChild, input, numberAttribute, computed } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DateTime } from 'luxon';
import JSZip from 'jszip';
import { Observable, of } from 'rxjs';
import { catchError, map, startWith, tap } from 'rxjs/operators';
import { PanModalComponent } from '../../../../components/pan-modal/pan-modal.component';
import { LocationSnapshotsService } from '../../../../service/location-snapshots.service';
import { NotifyService } from '../../../../service/notify.service';
import {
  CAMERA_SNAPSHOT_DETAILS_STATUS_OPTIONS,
  CameraSnapshotStatusOption,
  LocationSnapshotResponse,
  RhombusCameraSnapshotStatus,
  SnapshotRequestResponse,
  SnapshotScheduleResponse,
  Branch,
  LocationResponse,
  SnapshotCameraResponse,
} from '../../../../types';
import { linkedQueryParam } from 'ngxtension/linked-query-param';
import { cameraErrorMessage, sortBySnapshotError } from '../market-snapshots.utils';

type SnapshotImage = {
  requestDateTime: string;
  url: string | null;
  cameraName: string;
  image$: Observable<{ isLoading: boolean; error?: Error; value?: Blob }>;
  blob?: Blob;
  snapshotError?: string;
};

type PreviewCamera = {
  locationCameraId: string;
  cameraName: string;
  isLoading: boolean;
  error?: Error;
  image?: string;
};

type CameraSnapshotPreviewMode = 'live' | 'selected';

type SnapshotGroup = {
  id: string;
  requestDateTime: string;
  status: RhombusCameraSnapshotStatus;
  images: SnapshotImage[];
  comments: string;
};

@Component({
  selector: 'app-market-snapshot-detail',
  templateUrl: './market-snapshot-detail.component.html',
  styleUrls: ['./market-snapshot-detail.component.scss'],
  standalone: false,
})
export class MarketSnapshotDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private locationSnapshotsService = inject(LocationSnapshotsService);
  private notifyService = inject(NotifyService);

  public imageModal = viewChild<PanModalComponent>('imageModal');
  public requestNowModal = viewChild<PanModalComponent>('requestNowModal');

  public branchData = input<Branch>();
  public locationData = input<LocationResponse>();

  public branchId = input<number, unknown>(undefined, { transform: numberAttribute });
  public locationId = input<number, unknown>(undefined, { transform: numberAttribute });
  public locationName = signal<string>('');
  public branchName = signal<string>('');
  public locationCameras = signal<SnapshotCameraResponse[]>([]);
  public hasCameraMissingRegionOfInterest = computed(() => this.locationCameras().some((camera) => !camera.has_region_of_interest));
  public readonly cameraErrorMessage = cameraErrorMessage;
  public snapshotGroups = signal<SnapshotGroup[]>([]);
  public snapshotSchedules = signal<SnapshotScheduleResponse[]>([]);
  public isLoading = signal<boolean>(true);

  public isEditScheduleMode = signal<boolean>(false);
  public scheduleError = signal<string>('');
  private locationSchedules: SnapshotScheduleResponse[] = [];

  public selectedCamera = signal<{ cameraName: string; blob: Blob } | null>(null);

  public activePreviewCameraId = signal<string>('');
  public previewCameras = signal<PreviewCamera[]>([]);
  public activePreviewCam = computed(() => this.previewCameras().find((c) => c.locationCameraId === this.activePreviewCameraId()) ?? null);
  private previewZipBlob = signal<Blob | null>(null);
  private previewRequestDateTime = signal<string | null>(null);
  public isStorePhotosDisabled = computed(
    () => this.isStoringPhotos() || !this.previewCameras().some((c) => !!c.image) || this.previewCameras().some((c) => c.isLoading),
  );
  public isStoringPhotos = signal<boolean>(false);
  public photoPreviewMode = signal<'live' | 'selected'>('live');
  public selectedSnapshotTime = signal<string>('');

  public readonly snapshotStatusOptions: CameraSnapshotStatusOption[] = CAMERA_SNAPSHOT_DETAILS_STATUS_OPTIONS;

  public pageDateStr = linkedQueryParam<string>('date', {
    parse: (value) => (value ? DateTime.fromISO(value).toFormat('MM/dd/yyyy') : ''),
    stringify: (value) => (value ? DateTime.fromFormat(value, 'MM/dd/yyyy').toISODate()! : ''),
  });

  ngOnInit(): void {
    const dateParam = this.route.snapshot.queryParamMap.get('date');
    const snapshotDate = dateParam ? DateTime.fromISO(dateParam).toJSDate() : DateTime.now().toJSDate();

    this.loadSnapshotsForDate(snapshotDate);
  }

  public onSnapshotDateChange(offsetDays: number): void {
    const newDate = DateTime.fromFormat(this.pageDateStr(), 'MM/dd/yyyy').plus({ days: offsetDays });
    this.pageDateStr.set(newDate.toFormat('MM/dd/yyyy'));

    this.loadSnapshotsForDate(newDate.toJSDate());
  }

  private loadSnapshotsForDate(snapshotDate: Date): void {
    const locId = this.locationId();
    if (locId) {
      this.loadSnapshots(locId, snapshotDate);
    }
  }

  private loadSnapshots(locationId: number, snapshotDate: Date) {
    this.isLoading.set(true);

    this.locationSnapshotsService.getLocationSnapshots(locationId, snapshotDate).subscribe({
      next: (data: LocationSnapshotResponse[]) => {
        const locationData = data.find((d) => d.location_id === locationId);

        if (locationData) {
          this.locationName.set(locationData.location_name);
          this.branchName.set(locationData.branch_name);
          this.snapshotSchedules.set(locationData.schedules || []);
          this.locationSchedules = structuredClone(locationData.schedules || []);

          this.locationCameras.set(locationData.cameras);

          const groups: SnapshotGroup[] = [];
          const snapshotRequests = locationData.snapshot_requests || [];

          snapshotRequests.forEach((req) => {
            if (!req.snapshots?.length) return;
            groups.push(this.toSnapshotGroup(req, locationData.cameras));
          });

          this.snapshotGroups.set(groups);
        }

        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      },
    });
  }

  public openImageModal(image: SnapshotImage, imageRef: { value?: Blob }) {
    if (!imageRef?.value) return;
    this.selectedCamera.set({ cameraName: image.cameraName, blob: imageRef.value });
    this.imageModal()?.show();
  }

  private isDuplicateSnapshotTime(requestDateTime: string): boolean {
    const requested = DateTime.fromISO(requestDateTime).startOf('minute');
    return this.snapshotGroups().some((group) => DateTime.fromISO(group.requestDateTime).startOf('minute').equals(requested));
  }

  public openCameraSnapshotPreview(mode: CameraSnapshotPreviewMode) {
    this.photoPreviewMode.set(mode);
    this.selectedSnapshotTime.set('');
    this.activePreviewCameraId.set(this.locationCameras()[0]?.location_camera_id ?? '');
    this.previewCameras.set(
      this.locationCameras().map((c) => ({
        locationCameraId: c.location_camera_id,
        cameraName: c.camera_name,
        isLoading: mode === 'live',
      })),
    );
    this.requestNowModal()?.show();

    if (mode === 'live') {
      const tz = this.locationData()?.timezone ?? 'US/Eastern';
      const currentTime = DateTime.now().setZone(tz).toFormat('HH:mm');
      const liveDateTime = DateTime.fromFormat(`${this.pageDateStr()} ${currentTime}`, 'MM/dd/yyyy HH:mm').toISO({ includeOffset: false })!;

      if (this.isDuplicateSnapshotTime(liveDateTime)) {
        this.previewCameras.update((cams) => cams.map((c) => ({ ...c, isLoading: false })));
        this.notifyService.notify('A snapshot already exists for this time.');
        return;
      }

      this.fetchCameraSnapshots(liveDateTime);
    }
  }

  public loadSnapshotsForTime(time: string) {
    if (!time) return;
    const requestDateTime = DateTime.fromFormat(`${this.pageDateStr()} ${time}`, 'MM/dd/yyyy HH:mm').toISO({ includeOffset: false })!;

    if (this.isDuplicateSnapshotTime(requestDateTime)) {
      this.notifyService.notify('A snapshot already exists for this time. Please select a different time.');
      return;
    }

    this.previewCameras.update((cams) => cams.map((c) => ({ ...c, isLoading: true, image: undefined, error: undefined })));
    this.fetchCameraSnapshots(requestDateTime);
  }

  private fetchCameraSnapshots(requestDateTime: string) {
    const locId = this.locationId();
    if (!locId) return;

    const cameras = this.locationCameras().map((c) => ({
      location_camera_id: c.location_camera_id,
      provider_branch_id: c.provider_branch_id,
      provider_camera_id: c.provider_camera_id,
    }));

    this.locationSnapshotsService.getCameraSnapshots(locId, { cameras, request_date_time: requestDateTime }).subscribe({
      next: async (blob) => {
        this.previewZipBlob.set(blob);
        this.previewRequestDateTime.set(requestDateTime);
        const archive = await new JSZip().loadAsync(blob);
        await Promise.all(
          Object.keys(archive.files).map(async (filename) => {
            const locationCameraId = filename.split('--')[0];
            const image = await archive.files[filename].async('base64');
            this.previewCameras.update((cams) =>
              cams.map((c) => (c.locationCameraId === locationCameraId ? { ...c, isLoading: false, image } : c)),
            );
          }),
        );
        this.previewCameras.update((cams) => cams.map((c) => (c.isLoading ? { ...c, isLoading: false } : c)));
      },
      error: () => {
        this.previewCameras.update((cams) => cams.map((c) => ({ ...c, isLoading: false, error: new Error('Failed to load snapshot') })));
      },
    });
  }

  public saveSnapshotRequest() {
    const blob = this.previewZipBlob();
    const locId = this.locationId();
    const requestDateTime = this.previewRequestDateTime();
    if (!blob || !locId || !requestDateTime) return;

    this.isStoringPhotos.set(true);
    this.locationSnapshotsService.createLocationSnapshotRequest(locId, blob, requestDateTime).subscribe({
      next: (response: SnapshotRequestResponse) => {
        this.snapshotGroups.update((groups) => [this.toSnapshotGroup(response, this.locationCameras()), ...groups]);
        this.isStoringPhotos.set(false);
        this.notifyService.notify('Snapshots stored successfully!');
        this.requestNowModal()?.hide();
      },
      error: () => {
        this.isStoringPhotos.set(false);
      },
    });
  }

  private toSnapshotImage(url: string | null, cameraName: string, requestDateTime: string, snapshotError?: string): SnapshotImage {
    const imageObject: SnapshotImage = {
      url: url?.trim() ?? null,
      cameraName,
      requestDateTime,
      snapshotError,
      image$: of({ isLoading: true }),
    };

    if (snapshotError) {
      imageObject.image$ = of({ isLoading: false });
      return imageObject;
    }

    imageObject.image$ = this.locationSnapshotsService.getLocationSnapshot(imageObject.url!).pipe(
      map((value) => ({ isLoading: false, value, error: undefined })),
      catchError((error) => of({ isLoading: false, value: undefined, error })),
      startWith({ isLoading: true, value: undefined, error: undefined }),
      tap((result) => {
        if (result.value) imageObject.blob = result.value;
      }),
    );
    imageObject.image$.subscribe();
    return imageObject;
  }

  private toSnapshotGroup(req: SnapshotRequestResponse, cameras: SnapshotCameraResponse[]): SnapshotGroup {
    const images = req.snapshots.map((snap) => {
      const cameraName =
        cameras.find((c) => c.location_camera_id === snap.location_camera_id)?.camera_name || snap.location_camera_id || 'Unknown Camera';
      return this.toSnapshotImage(snap.url, cameraName, req.request_date_time, snap.snapshot_error);
    });
    sortBySnapshotError(images);
    return {
      id: req.id,
      requestDateTime: req.request_date_time,
      status: req.status,
      images,
      comments: req.comments || '',
    };
  }

  public updateSnapshotRequest(group: SnapshotGroup) {
    this.locationSnapshotsService
      .updateSnapshotRequest(this.locationId() as number, group.id, {
        status: group.status,
        comments: group.comments,
      })
      .subscribe({
        next: () => {
          this.notifyService.notify('Snapshot details updated successfully.');
        },
        error: () => {
          this.notifyService.notify('Unable to update snapshot details. Please try again.');
        },
      });
  }

  public setActivePreviewCamera(id: string) {
    this.activePreviewCameraId.set(id);
  }

  public toggleEditSchedule() {
    this.isEditScheduleMode.set(true);
    this.locationSchedules = structuredClone(this.snapshotSchedules());
  }

  public cancelEditSchedule() {
    this.isEditScheduleMode.set(false);
    this.snapshotSchedules.set(structuredClone(this.locationSchedules));
    this.scheduleError.set('');
  }

  public removeSchedule(index: number) {
    this.snapshotSchedules.update((schedules) => {
      const updated = [...schedules];
      updated.splice(index, 1);
      return updated;
    });
    this.validateSchedules();
  }

  public addSchedule() {
    this.snapshotSchedules.update((schedules) => [...schedules, { time: '12:00' }]);
    if (this.snapshotSchedules().length === 3) {
      this.notifyService.notify('Maximum 3 snapshot schedules are allowed.');
    }
  }

  public validateSchedules() {
    const schedules = this.snapshotSchedules();
    if (schedules.length === 0) {
      this.scheduleError.set('At least one schedule time is required.');
      return;
    }
    const times = schedules.map((s) => s.time?.trim()).filter(Boolean);
    if (times.length < schedules.length) {
      this.scheduleError.set('Schedule times cannot be empty.');
      return;
    }

    const outOfOrderIndex = times.findIndex((time, i) => i > 0 && time <= times[i - 1]);
    if (outOfOrderIndex !== -1) {
      this.scheduleError.set(`Time ${outOfOrderIndex + 1} must be later than Time ${outOfOrderIndex}.`);
      return;
    }

    this.scheduleError.set('');
  }

  public setSchedule() {
    this.validateSchedules();
    if (this.scheduleError()) {
      this.notifyService.notify(this.scheduleError());
      return;
    }

    const payloadSchedules = this.snapshotSchedules().map((s) => {
      const parsedTime = DateTime.fromISO(s.time);
      return {
        ...s,
        time: parsedTime.isValid ? parsedTime.toFormat('HH:mm:ss') : s.time,
      };
    });

    this.locationSnapshotsService.updateLocationSchedules(this.locationId() as number, { schedules: payloadSchedules }).subscribe({
      next: () => {
        this.isEditScheduleMode.set(false);
        this.locationSchedules = structuredClone(this.snapshotSchedules());
        this.notifyService.notify('Schedules updated successfully.');
      },
      error: () => {
        this.notifyService.notify('Unable to update schedules. Please try again.');
      },
    });
  }
}
